import {
  FieldArray,
  FieldArrayRenderProps,
  Form,
  Formik,
  FormikConfig,
  useFormikContext,
} from "formik";
import {
  Button,
  FormErrors,
  InputContainer,
  InputDate,
  InputNumber,
  InputSelect,
  InputSwitch,
  Label,
  Option,
} from "../components/form";
import { Dollar, Info, Percent, Trash } from "../icons";
import { Alert } from "../components/alert";
import { useCallback, useEffect, useRef, useState } from "react";
import { ILoan } from "./Loan";
import { Frequency } from "../applicant";
import { LendersData } from "../liabilities";
import { format, parse } from "date-fns";
import { SectionLabel } from "../components/label";
import { AnimatePresence, motion } from "framer-motion";
import { Collapse } from "../components/collapse";
import classNames from "classnames";
import { currencyAbbreviation } from "../util/formater";
import AddLoanForm from "./AddLoanForm";
import { isEmpty, isEqual } from "lodash";

type LoansFormProps = FormikConfig<any> & {
  initialValues: any;
  onSubmit: (values: any) => void;
  onChange: (values: any) => void;
  onEditing: (value: boolean) => void;
};

const frequency: {
  value: Frequency;
  label: string;
}[] = [
  {
    value: "Yearly",
    label: "Yearly",
  },
  {
    value: "HalfYearly",
    label: "Half-yearly",
  },
  {
    value: "Quarterly",
    label: "Quarterly",
  },
  {
    value: "Monthly",
    label: "Monthly",
  },
  {
    value: "Fortnightly",
    label: "Fortnightly",
  },
  {
    value: "Weekly",
    label: "Weekly",
  },
];

type LoanItemFormProps = {
  index: number;
  remove?: FieldArrayRenderProps["remove"];
  disabled?: boolean;
  onChange: (values: any) => void;
  onEditing: (status: boolean) => void;
};

const LoanItemForm = ({
  index,
  remove,
  disabled = false,
  onChange,
  onEditing,
}: LoanItemFormProps) => {
  const [deleting, setDeleting] = useState(false);
  const [showMore, setShowMore] = useState(false);
  const [showLoanTypeWarning, setShowLoanTypeWarning] = useState(false);
  const [LoanItem, setLoanItem] = useState<any>();
  const [hasErrors, setHasErrors] = useState<boolean>();

  const { values, setFieldValue } = useFormikContext<any>();

  const loanItemRef = useRef<HTMLTableSectionElement>(null);
  const [isOpen, setIsOpen] = useState(false);
  const toggleLender = useCallback(() => {
    setIsOpen((open) => !open);

    if (!isOpen) {
      setTimeout(() => {
        loanItemRef.current?.scrollIntoView({
          behavior: "smooth",
        });
      }, 200);
    }
  }, [isOpen]);

  useEffect(() => {
    if (disabled) setIsOpen(false);
  }, [disabled]);

  const TotalNewLoanAmount =
    (LoanItem?.Liability?.Limit || 0) +
    (!isNaN(LoanItem?.TopUpAmount) ? LoanItem?.TopUpAmount : 0);

  function handleLoanTypeChange(value: ILoan["LoanType"]) {
    if (LoanItem?.LoanType === "Existing" && value === "New") {
      setShowLoanTypeWarning(true);
      return;
    }
    handleLoanChange({
      ...LoanItem,
      LoanType: value,
    });
  }

  function removeLinkedLiability() {
    handleLoanChange({
      ...LoanItem,
      LoanType: "New",
    });
    setShowLoanTypeWarning(false);
  }

  const LoanType = LoanItem?.LoanType;
  const LoanAmount =
    LoanType === "New"
      ? LoanItem?.LoanAmount
      : !isNaN(LoanItem?.LoanAmount)
      ? LoanItem?.LoanAmount
      : LoanItem?.Liability?.Limit;
  const TopUpAmount = LoanItem?.TopUpAmount ?? 0;
  const TotalLoanAmount = LoanAmount + (!isNaN(TopUpAmount) ? TopUpAmount : 0);

  const Term = `${LoanItem?.TotalTermYears}yrs ${
    LoanItem?.TotalTermMonths ? `${LoanItem?.TotalTermMonths}mos` : ""
  }`;

  useEffect(() => {
    setLoanItem(values?.Loans[index]);
  }, [values?.Loans, index, setLoanItem]);

  const handleLoanChange = (NewLoanItem: any) => {
    setLoanItem(NewLoanItem);
    setHasErrors(!isEmpty(validateForm(NewLoanItem)));
    const editingValue = !isEqual(values?.Loans[index], NewLoanItem);
    onEditing?.(editingValue);
  };

  const hasValueChanged = !isEqual(values?.Loans[index], LoanItem);

  const handleSave = () => {
    onEditing?.(false);
    setFieldValue(`Loans[${index}]`, LoanItem);
    onChange?.({
      Loans: values?.Loans.map((_loan: any, idx: number) => {
        if (idx === index) {
          return LoanItem;
        }
        return _loan;
      }),
    });
  };

  const validateForm = (values: any) => {
    const errors: FormErrors<any> = {};

    if (!values.LoanType) {
      errors.LoanType = "Loan Type can't be empty";
    }

    if (values.LoanType === "New") {
      if (!values.LoanAmount) {
        errors.LoanAmount = "Loan Amount can't be empty or zero";
      }
      if (!values.TotalTermMonths && !values.TotalTermYears) {
        errors.TotalTermMonths = "Loan Term can't be empty or zero";
      }
    } else if (values.LoanType === "Existing") {
      if (!values.LendingAction) {
        errors.LendingAction = "Lending Action can't be empty";
      }

      if (values.LendingAction === "None") {
        if (!values.Liability?.RepaymentAmount) {
          errors.Liability = "Repayments can't be empty or zero";
        }
        if (!values.Liability?.Limit) {
          errors.LoanAmount = "Loan Amount can't be empty or zero";
        }
        if (!values.Liability?.Balance) {
          errors.Liability = "Balance can't be empty or zero";
        }
        if (!values.Liability?.InterestRate) {
          errors.Liability = "Interest rate can't be empty or zero";
        }
        if (
          !values.Liability?.TotalTermMonths &&
          !values.Liability?.TotalTermYears
        ) {
          errors.TotalTermMonths = "Loan Term can't be empty or zero";
        }
        if (values.Liability?.IsRevolvingCredit === null) {
          errors.Liability =
            "Revolving Credit / Redraw Facility can't be empty";
        }
      } else if (values.LendingAction === "Refinance") {
        if (!values.LoanAmount) {
          errors.LoanAmount = "Loan Amount can't be empty or zero";
        }
        if (!values?.TotalTermMonths && !values?.TotalTermYears) {
          errors.TotalTermMonths = "New Loan Term can't be empty or zero";
        }
      } else if (values.LendingAction === "TopUp") {
        if (!values.LoanAmount && !values.Liability?.Limit) {
          errors.LoanAmount = "Original Loan Amount can't be empty or zero";
        }
        if (!values.TopUpAmount) {
          errors.TopUpAmount = "Proposed Increaase can't be empty or zero";
        }
        if (
          !values.Liability?.TotalTermMonths &&
          !values.Liability?.TotalTermYears
        ) {
          errors.TotalTermMonths = "Original Loan Term can't be empty or zero";
        }
        if (!values.TotalTermMonths && !values.TotalTermYears) {
          errors.TotalTermMonths = "New Loan Term can't be empty or zero";
        }
        if (values.Lender) {
          if (!values.Liability?.InterestRate) {
            errors.Liability = "Current Interest Rate can't be empty or zero";
          }
          if (!values.Liability?.RepaymentAmount) {
            errors.Liability = " Current Repayments can't be empty or zero";
          }
          if (!values.Liability?.Balance) {
            errors.Liability = "Current Balance Rate can't be empty or zero";
          }
        }
      }
    }

    return errors;
  };

  return (
    <>
      <div
        className={classNames(
          "mb-[2px] flex items-center rounded-t-md bg-white px-[15px] py-[10px]",
          !isOpen && "rounded-b-md",
          disabled ? "cursor-not-allowed opacity-50" : "cursor-pointer"
        )}
        ref={loanItemRef}
        onClick={() => {
          if (!disabled) toggleLender();
        }}
      >
        <p className="flex flex-1 items-center gap-4 text-[14px] font-bold text-[#736878]">
          <span>Loan {index + 1}</span>
          {!isOpen && (
            <>
              <span
                className={classNames(
                  "h-[18px] w-[64px] rounded-[3px] p-0.5 text-center text-[10px] text-white",
                  LoanItem?.LoanType === "New"
                    ? "bg-[#42B4B4]"
                    : LoanItem?.LendingAction === "Refinance"
                    ? "bg-[#E29749]"
                    : LoanItem?.LendingAction === "TopUp"
                    ? "bg-[#577CF7]"
                    : "bg-[#9B88AA]"
                )}
              >
                {LoanItem?.LoanType === "New"
                  ? "NEW"
                  : LoanItem?.LendingAction === "Refinance"
                  ? "REFINANCE"
                  : LoanItem?.LendingAction === "TopUp"
                  ? "TOP-UP"
                  : "EXISTING"}
              </span>
              <span className="text-[13px] font-normal">
                {currencyAbbreviation(TotalLoanAmount)} / {Term}
              </span>
            </>
          )}
        </p>
        <Alert
          below={
            <Button
              className="-mr-2 w-max bg-transparent p-2 text-accent underline"
              size="small"
              onClick={(e) => {
                e.stopPropagation();
                setDeleting(true);
              }}
            >
              <Trash className="w-[10px] desktop:w-[14px]" />
            </Button>
          }
          open={deleting}
          backgroundColor="#1E1642"
          className="translate-y-1.5 text-left font-helvetica text-[11px] text-white shadow-none desktop:text-sm"
          arrowClassName="-translate-y-1 -translate-x-1"
          onClose={() => setDeleting(false)}
        >
          <p className="font-bold">Are you sure?</p>
          <p className="mt-[5px]">Deleting this item cannot be undone.</p>
          <div className="mt-3 flex items-center justify-end space-x-[28px]">
            <Button
              size="small"
              className="w-max bg-transparent text-white"
              onClick={() => setDeleting(false)}
            >
              <span className="font-helvetica text-[11px] leading-[1em] desktop:text-sm">
                Cancel
              </span>
            </Button>
            <Button
              className="w-max bg-[#E13756] px-4 py-2 font-bold text-white"
              size="small"
              onClick={(e) => {
                e.preventDefault();
                if (disabled) return;
                const _loanId = LoanItem?.LoanId;
                const newList = values?.Loans?.filter(
                  (_loan: any) => _loan?.LoanId !== _loanId
                );
                onChange?.({ Loans: newList });
                setFieldValue("Loans", newList);
                setDeleting(false);
                remove?.(index);
                e.preventDefault();
              }}
            >
              <span className="font-helvetica text-[11px] leading-[1em] desktop:text-sm">
                Yes, delete
              </span>
            </Button>
          </div>
        </Alert>
      </div>
      <Collapse collapse={!isOpen}>
        <div className="space-y-3 rounded-b-md bg-white p-[15px]">
          <InputContainer className="relative gap-0 border-transparent py-0">
            <Label htmlFor="" className="required">
              Loan Type
            </Label>
            <Alert
              open={showLoanTypeWarning}
              onClose={() => setShowLoanTypeWarning(false)}
              backgroundColor="#1E1642"
              wrapperClassName="min-w-[242px]"
              className="right-0 w-[400px] translate-x-0 translate-y-[10px] text-left font-helvetica text-[11px] text-white shadow-none desktop:text-sm"
              below={
                <InputSwitch
                  value={LoanItem?.LoanType}
                  onChange={handleLoanTypeChange}
                >
                  <InputSwitch.InputSwitchOption value="New">
                    NEW
                  </InputSwitch.InputSwitchOption>
                  <InputSwitch.InputSwitchOption value="Existing">
                    EXISTING
                  </InputSwitch.InputSwitchOption>
                </InputSwitch>
              }
            >
              <div className="flex items-center gap-3">
                <span>
                  <Info
                    width={48}
                    height={48}
                    fill="currentColor"
                    className="text-[#699AC8]"
                  />
                </span>
                <p>
                  Changing this loan from from 'Existing' to 'New' will remove
                  the linked liability.
                </p>
              </div>
              <div className="mt-3 flex items-center justify-end space-x-[28px]">
                <Button
                  type="button"
                  size="small"
                  className="w-max bg-transparent text-white"
                  onClick={() => setShowLoanTypeWarning(false)}
                >
                  <span className="font-helvetica text-[11px] leading-[1em] desktop:text-sm">
                    Cancel
                  </span>
                </Button>
                <Button
                  type="button"
                  className="w-[100px] px-4 py-2"
                  size="small"
                  onClick={removeLinkedLiability}
                >
                  <span className="font-helvetica text-[11px] font-bold leading-[1em] desktop:text-sm">
                    OK
                  </span>
                </Button>
              </div>
            </Alert>
          </InputContainer>
          {LoanItem?.LoanType === "New" && (
            <>
              <InputContainer className="gap-0 border-transparent py-0">
                <Label htmlFor="LoanAmount" className="required">
                  Loan Amount
                </Label>
                <InputNumber
                  id="LoanAmount"
                  buttons={false}
                  value={LoanItem?.LoanAmount}
                  onChange={(value) =>
                    handleLoanChange({
                      ...LoanItem,
                      LoanAmount: value,
                    })
                  }
                  withIcon={<Dollar />}
                  className="min-w-[242px]"
                  autonumeric
                />
              </InputContainer>
              <InputContainer className="gap-0 border-transparent py-0">
                <Label htmlFor="LoanTermYears" className="required">
                  Loan Term
                </Label>
                <div className="grid min-w-[242px] grid-cols-2 gap-2">
                  <div className="relative">
                    <InputNumber
                      id="LoanTermYears"
                      value={LoanItem?.TotalTermYears}
                      onChange={(value) =>
                        handleLoanChange({
                          ...LoanItem,
                          TotalTermYears: value,
                        })
                      }
                      buttons={false}
                      placeholder="0"
                      pattern="^[0-9]*$"
                    />
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                      YRS
                    </span>
                  </div>
                  <div className="relative">
                    <InputNumber
                      id="LoanTermMonths"
                      value={LoanItem?.TotalTermMonths ?? undefined}
                      onChange={(value) =>
                        handleLoanChange({
                          ...LoanItem,
                          TotalTermMonths: value,
                        })
                      }
                      buttons={false}
                      placeholder="0"
                      pattern="^[0-9]*$"
                    />
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                      MOS
                    </span>
                  </div>
                </div>
              </InputContainer>
              <InputContainer className="gap-0 border-transparent py-0">
                <Label htmlFor="InterestOnlyTermYears">
                  Interest-Only Term
                </Label>
                <div className="grid min-w-[242px] grid-cols-2 gap-2">
                  <div className="relative">
                    <InputNumber
                      id="InterestOnlyTermYears"
                      value={LoanItem?.InterestOnlyTermYears ?? undefined}
                      onChange={(value) =>
                        handleLoanChange({
                          ...LoanItem,
                          InterestOnlyTermYears: value,
                        })
                      }
                      buttons={false}
                      placeholder="0"
                      pattern="^[0-9]*$"
                    />
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                      YRS
                    </span>
                  </div>
                  <div className="relative">
                    <InputNumber
                      id="InterestOnlyTermMonths"
                      value={LoanItem?.InterestOnlyTermMonths ?? undefined}
                      onChange={(value) =>
                        handleLoanChange({
                          ...LoanItem,
                          InterestOnlyTermMonths: value,
                        })
                      }
                      buttons={false}
                      placeholder="0"
                      pattern="^[0-9]*$"
                    />
                    <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                      MOS
                    </span>
                  </div>
                </div>
              </InputContainer>
            </>
          )}
          {LoanItem?.LoanType === "Existing" && (
            <>
              <InputContainer className="gap-0 border-transparent py-0">
                <Label htmlFor="LendingAction" className="required">
                  Lending Action
                </Label>
                <InputSelect
                  className="min-w-[242px]"
                  initialValue={LoanItem?.LendingAction}
                  onChange={(value) => {
                    let newValue = {
                      ...LoanItem,
                      LendingAction: value,
                    };
                    if (value === "TopUp" && !LoanItem?.Liability?.Limit) {
                      newValue = {
                        ...newValue,
                        Liability: {
                          ...newValue?.Liability,
                          Limit: LoanItem.LoanAmount,
                        },
                      };
                    }
                    handleLoanChange(newValue);
                  }}
                >
                  <Option value={"None"}>None</Option>
                  <Option value={"Refinance"}>Refinance</Option>
                  <Option value={"TopUp"}>Top-Up</Option>
                </InputSelect>
              </InputContainer>
              {LoanItem?.LendingAction === null && (
                <>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="LoanAmount" className="required">
                      Loan Amount
                    </Label>
                    <InputNumber
                      id="LoanAmount"
                      buttons={false}
                      value={LoanItem?.LoanAmount}
                      onChange={(value) =>
                        handleLoanChange({
                          ...LoanItem,
                          LoanAmount: value,
                        })
                      }
                      withIcon={<Dollar />}
                      className="min-w-[242px]"
                      autonumeric
                    />
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="LoanTermYears" className="required">
                      Loan Term
                    </Label>
                    <div className="grid min-w-[242px] grid-cols-2 gap-2">
                      <div className="relative">
                        <InputNumber
                          id="LoanTermYears"
                          value={LoanItem?.TotalTermYears}
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              TotalTermYears: value,
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          YRS
                        </span>
                      </div>
                      <div className="relative">
                        <InputNumber
                          id="LoanTermMonths"
                          value={LoanItem?.TotalTermMonths ?? undefined}
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              TotalTermMonths: value,
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          MOS
                        </span>
                      </div>
                    </div>
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="InterestOnlyTermYears">
                      Interest-Only Term
                    </Label>
                    <div className="grid min-w-[242px] grid-cols-2 gap-2">
                      <div className="relative">
                        <InputNumber
                          id="InterestOnlyTermYears"
                          value={LoanItem?.InterestOnlyTermYears}
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              InterestOnlyTermYears: value,
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          YRS
                        </span>
                      </div>
                      <div className="relative">
                        <InputNumber
                          id="InterestOnlyTermMonths"
                          value={LoanItem?.InterestOnlyTermMonths ?? undefined}
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              InterestOnlyTermMonths: value,
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          MOS
                        </span>
                      </div>
                    </div>
                  </InputContainer>
                </>
              )}
              {LoanItem?.LendingAction === "None" && (
                <>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="LendingAction" className="required">
                      Repayments
                    </Label>
                    <div className="flex min-w-[242px] items-center rounded-[3px] border border-[#C2B6CB]">
                      <InputNumber
                        id="RepaymentAmount"
                        withIcon={<Dollar />}
                        value={
                          LoanItem?.Liability?.RepaymentAmount || undefined
                        }
                        className="rounded-none border-transparent border-r-[#C2B6CB] focus:!outline-none"
                        onChange={(value) => {
                          handleLoanChange({
                            ...LoanItem,
                            Liability: {
                              ...LoanItem?.Liability,
                              RepaymentAmount: value,
                            },
                          });
                        }}
                        autonumeric
                      />
                      <InputSelect
                        initialValue={LoanItem?.Liability?.RepaymentFrequency}
                        onChange={(value) =>
                          handleLoanChange({
                            ...LoanItem,
                            Liability: {
                              ...LoanItem?.Liability,
                              RepaymentFrequency: value,
                            },
                          })
                        }
                        className="w-[50px]"
                        border="border-0"
                        formatButtonLabel={(value) => value?.charAt(0)}
                      >
                        <Option value={null}></Option>
                        {frequency.map((freq) => (
                          <Option value={freq.value} key={freq.value}>
                            {freq.label}
                          </Option>
                        ))}
                      </InputSelect>
                    </div>
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="Limit" className="required">
                      Loan Amount
                    </Label>
                    <InputNumber
                      id="Limit"
                      buttons={false}
                      value={LoanItem?.Liability?.Limit ?? NaN}
                      onChange={(value) =>
                        handleLoanChange({
                          ...LoanItem,
                          LoanAmount: value,
                          Liability: {
                            ...LoanItem?.Liability,
                            Limit: value,
                          },
                        })
                      }
                      withIcon={<Dollar />}
                      className="min-w-[242px]"
                      autonumeric
                    />
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="Balance" className="required">
                      Balance
                    </Label>
                    <InputNumber
                      id="Balance"
                      buttons={false}
                      value={LoanItem?.Liability?.Balance ?? NaN}
                      onChange={(value) =>
                        handleLoanChange({
                          ...LoanItem,
                          Liability: {
                            ...LoanItem?.Liability,
                            Balance: value,
                          },
                        })
                      }
                      withIcon={<Dollar />}
                      className="min-w-[242px]"
                      autonumeric
                    />
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="Lender">Lender</Label>
                    <InputSelect
                      initialValue={LoanItem?.Liability?.Lender}
                      onChange={(value) =>
                        handleLoanChange({
                          ...LoanItem,
                          Liability: {
                            ...LoanItem?.Liability,
                            Lender: value,
                          },
                        })
                      }
                      className="min-w-[242px]"
                      searchable={{ placeholder: "Search Lender" }}
                    >
                      <Option value={null}></Option>
                      {LendersData.map((lender) => (
                        <Option value={lender.Code} key={lender.Code}>
                          {lender.Name}
                        </Option>
                      ))}
                    </InputSelect>
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="RepaymentType">Repayment Type</Label>
                    <InputSelect
                      initialValue={LoanItem?.Liability?.RepaymentType}
                      onChange={(value) =>
                        handleLoanChange({
                          ...LoanItem,
                          Liability: {
                            ...LoanItem?.Liability,
                            RepaymentType: value,
                          },
                        })
                      }
                      className="min-w-[242px]"
                    >
                      <Option value={null}></Option>
                      {[
                        {
                          value: "PrincipalInterest",
                          label: "Principal & Interest",
                        },
                        {
                          value: "InterestOnly",
                          label: "Interest-Only",
                        },
                      ].map((type) => (
                        <Option value={type.value} key={type.value}>
                          {type.label}
                        </Option>
                      ))}
                    </InputSelect>
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="InterestRate" className="required">
                      Interest Rate
                    </Label>
                    <InputNumber
                      id="InterestRate"
                      className="min-w-[242px]"
                      withIcon={<Percent className="opacity-50" />}
                      iconPosition="right"
                      value={LoanItem?.Liability?.InterestRate || undefined}
                      onChange={(value) =>
                        handleLoanChange({
                          ...LoanItem,
                          Liability: {
                            ...LoanItem?.Liability,
                            InterestRate: value,
                          },
                        })
                      }
                    />
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="StartDate">Start Date</Label>
                    <InputDate
                      id="StartDate"
                      wrapperClassName="min-w-[242px]"
                      placeholder="DD / MM / YYYY"
                      value={
                        LoanItem?.Liability?.StartDate
                          ? parse(
                              LoanItem?.Liability.StartDate,
                              "yyyy-MM-dd",
                              new Date()
                            )
                          : undefined
                      }
                      onChange={(date) =>
                        handleLoanChange({
                          ...LoanItem,
                          Liability: {
                            ...LoanItem?.Liability,
                            StartDate: format(date, "yyyy-MM-dd"),
                          },
                        })
                      }
                    />
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="LoanTermYears" className="required">
                      Loan Term
                    </Label>
                    <div className="grid min-w-[242px] grid-cols-2 gap-2">
                      <div className="relative">
                        <InputNumber
                          id="LoanTermYears"
                          value={
                            LoanItem?.Liability?.TotalTermYears ?? undefined
                          }
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              Liability: {
                                ...LoanItem?.Liability,
                                TotalTermYears: value,
                              },
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          YRS
                        </span>
                      </div>
                      <div className="relative">
                        <InputNumber
                          id="LoanTermMonths"
                          value={
                            LoanItem?.Liability?.TotalTermMonths ?? undefined
                          }
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              Liability: {
                                ...LoanItem?.Liability,
                                TotalTermMonths: value,
                              },
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          MOS
                        </span>
                      </div>
                    </div>
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="InterestOnlyTermYears">
                      Interest-Only Term
                    </Label>
                    <div className="grid min-w-[242px] grid-cols-2 gap-2">
                      <div className="relative">
                        <InputNumber
                          id="InterestOnlyTermYears"
                          value={
                            LoanItem?.Liability?.InterestOnlyTermYears ?? 0
                          }
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              Liability: {
                                ...LoanItem?.Liability,
                                InterestOnlyTermYears: value,
                              },
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          YRS
                        </span>
                      </div>
                      <div className="relative">
                        <InputNumber
                          id="InterestOnlyTermMonths"
                          value={
                            LoanItem?.Liability?.InterestOnlyTermMonths ??
                            undefined
                          }
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              Liability: {
                                ...LoanItem?.Liability,
                                InterestOnlyTermMonths: value,
                              },
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          MOS
                        </span>
                      </div>
                    </div>
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="IsRevolvingCredit" className="required">
                      Revolving Credit / Redraw Facility
                    </Label>
                    <InputSwitch
                      id="IsRevolvingCredit"
                      value={LoanItem?.Liability?.IsRevolvingCredit}
                      onChange={(value) =>
                        handleLoanChange({
                          ...LoanItem,
                          Liability: {
                            ...LoanItem?.Liability,
                            IsRevolvingCredit: value,
                          },
                        })
                      }
                      className="min-w-[242px]"
                    >
                      <InputSwitch.InputSwitchOption value={true}>
                        Yes
                      </InputSwitch.InputSwitchOption>
                      <InputSwitch.InputSwitchOption value={false}>
                        No
                      </InputSwitch.InputSwitchOption>
                    </InputSwitch>
                  </InputContainer>
                </>
              )}
              {LoanItem?.LendingAction === "Refinance" && (
                <>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="LoanAmount" className="required">
                      Loan Amount
                    </Label>
                    <InputNumber
                      id="LoanAmount"
                      buttons={false}
                      value={
                        !isNaN(LoanItem?.LoanAmount)
                          ? LoanItem?.LoanAmount
                          : LoanItem?.Liability?.Limit || undefined
                      }
                      onChange={(value) => {
                        const newValue = {
                          ...LoanItem,
                          LoanAmount: value,
                          Liability: {
                            ...LoanItem?.Liability,
                            Limit: value,
                          },
                        };
                        handleLoanChange(newValue);
                      }}
                      withIcon={<Dollar />}
                      className="min-w-[242px]"
                      autonumeric
                    />
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="OriginalLoanTermYears">
                      Original Loan Term
                    </Label>
                    <div className="grid min-w-[242px] grid-cols-2 gap-2">
                      <div className="relative">
                        <InputNumber
                          id="OriginalLoanTermYears"
                          value={
                            LoanItem?.Liability?.TotalTermYears ?? undefined
                          }
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              TotalTermYears: value,
                              Liability: {
                                ...LoanItem?.Liability,
                                TotalTermYears: value,
                              },
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          YRS
                        </span>
                      </div>
                      <div className="relative">
                        <InputNumber
                          id="OriginalLoanTermMonths"
                          value={
                            LoanItem?.Liability?.TotalTermMonths ?? undefined
                          }
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              TotalTermMonths: value,
                              Liability: {
                                ...LoanItem?.Liability,
                                TotalTermMonths: value,
                              },
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          MOS
                        </span>
                      </div>
                    </div>
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="LoanTermYears" className="required">
                      New Loan Term
                    </Label>
                    <div className="grid min-w-[242px] grid-cols-2 gap-2">
                      <div className="relative">
                        <InputNumber
                          id="LoanTermYears"
                          value={LoanItem?.TotalTermYears}
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              TotalTermYears: value,
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          YRS
                        </span>
                      </div>
                      <div className="relative">
                        <InputNumber
                          id="LoanTermMonths"
                          value={LoanItem?.TotalTermMonths ?? undefined}
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              TotalTermMonths: value,
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          MOS
                        </span>
                      </div>
                    </div>
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="InterestOnlyTermYears">New IO Term</Label>
                    <div className="grid min-w-[242px] grid-cols-2 gap-2">
                      <div className="relative">
                        <InputNumber
                          id="InterestOnlyTermYears"
                          value={LoanItem?.InterestOnlyTermYears ?? 0}
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              InterestOnlyTermYears: value,
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          YRS
                        </span>
                      </div>
                      <div className="relative">
                        <InputNumber
                          id="InterestOnlyTermMonths"
                          value={LoanItem?.InterestOnlyTermMonths ?? undefined}
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              InterestOnlyTermMonths: value,
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          MOS
                        </span>
                      </div>
                    </div>
                  </InputContainer>
                  <InputContainer className="justify-end gap-0 border-transparent py-0">
                    <Label
                      htmlFor={"ShowMore" + LoanItem.LoanId}
                      className="w-[242px] font-bold text-accent"
                    >
                      {showMore ? "Show Less" : "Show More"}...
                    </Label>
                    <input
                      checked={showMore}
                      onChange={() => setShowMore(!showMore)}
                      type="checkbox"
                      className="hidden"
                      id={"ShowMore" + LoanItem.LoanId}
                      name={"ShowMore" + LoanItem.LoanId}
                    />
                  </InputContainer>
                  <AnimatePresence>
                    {showMore && (
                      <motion.div
                        initial={{
                          height: 0,
                          opacity: 0,
                        }}
                        animate={{
                          height: "auto",
                          opacity: 1,
                        }}
                        exit={{ height: 0, opacity: 0, marginTop: 0 }}
                        transition={{ duration: 0.2, ease: "linear" }}
                        className="!mt-0 space-y-2"
                      >
                        <SectionLabel className="!text-[13px] desktop:!text-sm">
                          Existing Loan Details
                        </SectionLabel>
                        <InputContainer className="gap-0 border-transparent py-0">
                          <Label htmlFor="Lender">Lender</Label>
                          <InputSelect
                            initialValue={LoanItem?.Lender}
                            onChange={(value) => {
                              const newValue = {
                                ...LoanItem,
                                Lender: value,
                                Liability: {
                                  ...LoanItem?.Liability,
                                  Lender: value,
                                },
                              };
                              handleLoanChange(newValue);
                            }}
                            className="min-w-[242px]"
                            searchable={{ placeholder: "Search Lender" }}
                          >
                            <Option value={null}></Option>
                            {LendersData.map((lender) => (
                              <Option value={lender.Code} key={lender.Code}>
                                {lender.Name}
                              </Option>
                            ))}
                          </InputSelect>
                        </InputContainer>
                        <InputContainer className="gap-0 border-transparent py-0">
                          <Label htmlFor="LendingAction">Repayments</Label>
                          <div className="flex min-w-[242px] items-center rounded-[3px] border border-[#C2B6CB]">
                            <InputNumber
                              id="RepaymentAmount"
                              withIcon={<Dollar />}
                              value={
                                LoanItem?.Liability?.RepaymentAmount ||
                                undefined
                              }
                              className="rounded-none border-transparent border-r-[#C2B6CB] focus:!outline-none"
                              onChange={(value) => {
                                handleLoanChange({
                                  ...LoanItem,
                                  Liability: {
                                    ...LoanItem?.Liability,
                                    RepaymentAmount: value,
                                  },
                                });
                              }}
                              autonumeric
                            />
                            <InputSelect
                              initialValue={
                                LoanItem?.Liability?.RepaymentFrequency
                              }
                              onChange={(value) =>
                                handleLoanChange({
                                  ...LoanItem,
                                  Liability: {
                                    ...LoanItem?.Liability,
                                    RepaymentFrequency: value,
                                  },
                                })
                              }
                              className="w-[50px]"
                              border="border-0"
                              formatButtonLabel={(value) => value?.charAt(0)}
                            >
                              <Option value={null}></Option>
                              {frequency.map((freq) => (
                                <Option value={freq.value} key={freq.value}>
                                  {freq.label}
                                </Option>
                              ))}
                            </InputSelect>
                          </div>
                        </InputContainer>
                        <InputContainer className="gap-0 border-transparent py-0">
                          <Label htmlFor="Balance">Balance</Label>
                          <InputNumber
                            id="Balance"
                            buttons={false}
                            value={LoanItem?.Liability?.Balance ?? NaN}
                            onChange={(value) =>
                              handleLoanChange({
                                ...LoanItem,
                                Liability: {
                                  ...LoanItem?.Liability,
                                  Balance: value,
                                },
                              })
                            }
                            withIcon={<Dollar />}
                            className="min-w-[242px]"
                            autonumeric
                          />
                        </InputContainer>
                        <InputContainer className="gap-0 border-transparent py-0">
                          <Label htmlFor="InterestRate">Interest Rate</Label>
                          <InputNumber
                            id="InterestRate"
                            className="min-w-[242px]"
                            withIcon={<Percent className="opacity-50" />}
                            iconPosition="right"
                            value={
                              LoanItem?.Liability?.InterestRate || undefined
                            }
                            onChange={(value) =>
                              handleLoanChange({
                                ...LoanItem,
                                Liability: {
                                  ...LoanItem?.Liability,
                                  InterestRate: value,
                                },
                              })
                            }
                          />
                        </InputContainer>
                        <InputContainer className="gap-0 border-transparent py-0">
                          <Label htmlFor="RepaymentType">Repayment Type</Label>
                          <InputSelect
                            initialValue={LoanItem?.Liability?.RepaymentType}
                            onChange={(value) =>
                              handleLoanChange({
                                ...LoanItem,
                                Liability: {
                                  ...LoanItem?.Liability,
                                  RepaymentType: value,
                                },
                              })
                            }
                            className="min-w-[242px]"
                          >
                            <Option value={null}></Option>
                            {[
                              {
                                value: "PrincipalInterest",
                                label: "Principal & Interest",
                              },
                              {
                                value: "InterestOnly",
                                label: "Interest-Only",
                              },
                            ].map((type) => (
                              <Option value={type.value} key={type.value}>
                                {type.label}
                              </Option>
                            ))}
                          </InputSelect>
                        </InputContainer>
                        <InputContainer className="gap-0 border-transparent py-0">
                          <Label htmlFor="StartDate">Start Date</Label>
                          <InputDate
                            id="StartDate"
                            wrapperClassName="min-w-[242px]"
                            placeholder="DD / MM / YYYY"
                            value={
                              LoanItem?.Liability?.StartDate
                                ? parse(
                                    LoanItem?.Liability.StartDate,
                                    "yyyy-MM-dd",
                                    new Date()
                                  )
                                : undefined
                            }
                            onChange={(date) =>
                              handleLoanChange({
                                ...LoanItem,
                                Liability: {
                                  ...LoanItem?.Liability,
                                  StartDate: format(date, "yyyy-MM-dd"),
                                },
                              })
                            }
                          />
                        </InputContainer>
                        <InputContainer className="gap-0 border-transparent py-0">
                          <Label htmlFor="InterestOnlyTermYears">
                            Interest-Only Term
                          </Label>
                          <div className="grid min-w-[242px] grid-cols-2 gap-2">
                            <div className="relative">
                              <InputNumber
                                id="InterestOnlyTermYears"
                                value={
                                  LoanItem?.Liability?.InterestOnlyTermYears ??
                                  0
                                }
                                onChange={(value) =>
                                  handleLoanChange({
                                    ...LoanItem,
                                    Liability: {
                                      ...LoanItem?.Liability,
                                      InterestOnlyTermYears: value,
                                    },
                                  })
                                }
                                buttons={false}
                                placeholder="0"
                                pattern="^[0-9]*$"
                              />
                              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                                YRS
                              </span>
                            </div>
                            <div className="relative">
                              <InputNumber
                                id="InterestOnlyTermMonths"
                                value={
                                  LoanItem?.Liability?.InterestOnlyTermMonths ??
                                  undefined
                                }
                                onChange={(value) =>
                                  handleLoanChange({
                                    ...LoanItem,
                                    Liability: {
                                      ...LoanItem?.Liability,
                                      InterestOnlyTermMonths: value,
                                    },
                                  })
                                }
                                buttons={false}
                                placeholder="0"
                                pattern="^[0-9]*$"
                              />
                              <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                                MOS
                              </span>
                            </div>
                          </div>
                        </InputContainer>
                        <InputContainer className="gap-0 border-transparent py-0">
                          <Label htmlFor="IsRevolvingCredit">
                            Revolving Credit / Redraw Facility
                          </Label>
                          <InputSwitch
                            id="IsRevolvingCredit"
                            value={LoanItem?.Liability?.IsRevolvingCredit}
                            onChange={(value) =>
                              handleLoanChange({
                                ...LoanItem,
                                Liability: {
                                  ...LoanItem?.Liability,
                                  IsRevolvingCredit: value,
                                },
                              })
                            }
                            className="min-w-[242px]"
                          >
                            <InputSwitch.InputSwitchOption value={true}>
                              Yes
                            </InputSwitch.InputSwitchOption>
                            <InputSwitch.InputSwitchOption value={false}>
                              No
                            </InputSwitch.InputSwitchOption>
                          </InputSwitch>
                        </InputContainer>
                      </motion.div>
                    )}
                  </AnimatePresence>
                </>
              )}
              {LoanItem?.LendingAction === "TopUp" && (
                <>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="LoanAmount" className="required">
                      Original Loan Amount
                    </Label>
                    <InputNumber
                      id="LoanAmount"
                      buttons={false}
                      value={
                        !isNaN(LoanItem?.LoanAmount)
                          ? LoanItem?.LoanAmount
                          : LoanItem?.Liability?.Limit || undefined
                      }
                      onChange={(value) => {
                        handleLoanChange({
                          ...LoanItem,
                          LoanAmount: value,
                          Liability: {
                            ...LoanItem?.Liability,
                            Limit: value,
                          },
                        });
                      }}
                      withIcon={<Dollar />}
                      className="min-w-[242px]"
                      autonumeric
                    />
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="TopUpAmount" className="required">
                      Proposed Increase
                    </Label>
                    <InputNumber
                      id="TopUpAmount"
                      buttons={false}
                      value={LoanItem?.TopUpAmount}
                      onChange={(value) => {
                        handleLoanChange({
                          ...LoanItem,
                          TopUpAmount: value,
                        });
                      }}
                      withIcon={<Dollar />}
                      className="min-w-[242px]"
                      autonumeric
                    />
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="NewLoanTotal">New Loan Total</Label>
                    <InputNumber
                      key={TotalNewLoanAmount}
                      id="NewLoanTotal"
                      buttons={false}
                      value={TotalNewLoanAmount}
                      withIcon={<Dollar />}
                      className="min-w-[242px]"
                      disabled
                      autonumeric
                    />
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="OriginalLoanTermYears" className="required">
                      Original Loan Term
                    </Label>
                    <div className="grid min-w-[242px] grid-cols-2 gap-2">
                      <div className="relative">
                        <InputNumber
                          id="OriginalLoanTermYears"
                          value={
                            LoanItem?.Liability?.TotalTermYears ?? undefined
                          }
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              TotalTermYears: value,
                              Liability: {
                                ...LoanItem?.Liability,
                                TotalTermYears: value,
                              },
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          YRS
                        </span>
                      </div>
                      <div className="relative">
                        <InputNumber
                          id="OriginalLoanTermMonths"
                          value={
                            LoanItem?.Liability?.TotalTermMonths ?? undefined
                          }
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              TotalTermMonths: value,
                              Liability: {
                                ...LoanItem?.Liability,
                                TotalTermMonths: value,
                              },
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          MOS
                        </span>
                      </div>
                    </div>
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="LoanTermYears" className="required">
                      New Loan Term
                    </Label>
                    <div className="grid min-w-[242px] grid-cols-2 gap-2">
                      <div className="relative">
                        <InputNumber
                          id="LoanTermYears"
                          value={LoanItem?.TotalTermYears}
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              TotalTermYears: value,
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          YRS
                        </span>
                      </div>
                      <div className="relative">
                        <InputNumber
                          id="LoanTermMonths"
                          value={LoanItem?.TotalTermMonths ?? undefined}
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              TotalTermMonths: value,
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          MOS
                        </span>
                      </div>
                    </div>
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="InterestOnlyTermYears">New IO Term</Label>
                    <div className="grid min-w-[242px] grid-cols-2 gap-2">
                      <div className="relative">
                        <InputNumber
                          id="InterestOnlyTermYears"
                          value={LoanItem?.InterestOnlyTermYears ?? 0}
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              InterestOnlyTermYears: value,
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          YRS
                        </span>
                      </div>
                      <div className="relative">
                        <InputNumber
                          id="InterestOnlyTermMonths"
                          value={LoanItem?.InterestOnlyTermMonths ?? undefined}
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              InterestOnlyTermMonths: value,
                            })
                          }
                          buttons={false}
                          placeholder="0"
                          pattern="^[0-9]*$"
                        />
                        <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                          MOS
                        </span>
                      </div>
                    </div>
                  </InputContainer>
                  <InputContainer className="gap-0 border-transparent py-0">
                    <Label htmlFor="Lender">Current Lender</Label>
                    <InputSelect
                      initialValue={LoanItem?.Lender ?? null}
                      onChange={(value) => {
                        const newValue = {
                          ...LoanItem,
                          Lender: value,
                          Liability: {
                            ...LoanItem?.Liability,
                            Lender: value,
                          },
                        };
                        handleLoanChange(newValue);
                      }}
                      className="min-w-[242px]"
                      searchable={{ placeholder: "Search Lender" }}
                    >
                      <Option value={null}></Option>
                      {LendersData.map((lender) => (
                        <Option value={lender.Code} key={lender.Code}>
                          {lender.Name}
                        </Option>
                      ))}
                    </InputSelect>
                  </InputContainer>
                  {LoanItem?.Lender === null && (
                    <>
                      <InputContainer className="justify-end gap-0 border-transparent py-0">
                        <Label
                          htmlFor={"ShowMore" + LoanItem.LoanId}
                          className="w-[242px] font-bold text-accent"
                        >
                          {showMore ? "Show Less" : "Show More"}...
                        </Label>
                        <input
                          checked={showMore}
                          onChange={() => setShowMore(!showMore)}
                          type="checkbox"
                          className="hidden"
                          id={"ShowMore" + LoanItem.LoanId}
                          name={"ShowMore" + LoanItem.LoanId}
                        />
                      </InputContainer>
                      <AnimatePresence>
                        {showMore && (
                          <motion.div
                            initial={{
                              height: 0,
                              opacity: 0,
                            }}
                            animate={{
                              height: "auto",
                              opacity: 1,
                            }}
                            exit={{ height: 0, opacity: 0, marginTop: 0 }}
                            transition={{ duration: 0.2, ease: "linear" }}
                            className="!mt-0 space-y-2"
                          >
                            <SectionLabel className="!text-[13px] desktop:!text-sm">
                              Existing Loan Details
                            </SectionLabel>
                            <InputContainer className="gap-0 border-transparent py-0">
                              <Label htmlFor="LendingAction">Repayments</Label>
                              <div className="flex min-w-[242px] items-center rounded-[3px] border border-[#C2B6CB]">
                                <InputNumber
                                  id="RepaymentAmount"
                                  withIcon={<Dollar />}
                                  value={
                                    LoanItem?.Liability?.RepaymentAmount ||
                                    undefined
                                  }
                                  className="rounded-none border-transparent border-r-[#C2B6CB] focus:!outline-none"
                                  onChange={(value) => {
                                    handleLoanChange({
                                      ...LoanItem,
                                      Liability: {
                                        ...LoanItem?.Liability,
                                        RepaymentAmount: value,
                                      },
                                    });
                                  }}
                                  autonumeric
                                />
                                <InputSelect
                                  initialValue={
                                    LoanItem?.Liability?.RepaymentFrequency
                                  }
                                  onChange={(value) =>
                                    handleLoanChange({
                                      ...LoanItem,
                                      Liability: {
                                        ...LoanItem?.Liability,
                                        RepaymentFrequency: value,
                                      },
                                    })
                                  }
                                  className="w-[50px]"
                                  border="border-0"
                                  formatButtonLabel={(value) =>
                                    value?.charAt(0)
                                  }
                                >
                                  <Option value={null}></Option>
                                  {frequency.map((freq) => (
                                    <Option value={freq.value} key={freq.value}>
                                      {freq.label}
                                    </Option>
                                  ))}
                                </InputSelect>
                              </div>
                            </InputContainer>
                            <InputContainer className="gap-0 border-transparent py-0">
                              <Label htmlFor="Balance">Balance</Label>
                              <InputNumber
                                id="Balance"
                                buttons={false}
                                value={LoanItem?.Liability?.Balance ?? NaN}
                                onChange={(value) =>
                                  handleLoanChange({
                                    ...LoanItem,
                                    Liability: {
                                      ...LoanItem?.Liability,
                                      Balance: value,
                                    },
                                  })
                                }
                                withIcon={<Dollar />}
                                className="min-w-[242px]"
                                autonumeric
                              />
                            </InputContainer>
                            <InputContainer className="gap-0 border-transparent py-0">
                              <Label htmlFor="InterestRate">
                                Interest Rate
                              </Label>
                              <InputNumber
                                id="InterestRate"
                                className="min-w-[242px]"
                                withIcon={<Percent className="opacity-50" />}
                                iconPosition="right"
                                value={LoanItem?.Liability?.InterestRate || 0}
                                onChange={(value) =>
                                  handleLoanChange({
                                    ...LoanItem,
                                    Liability: {
                                      ...LoanItem?.Liability,
                                      InterestRate: value,
                                    },
                                  })
                                }
                              />
                            </InputContainer>
                            <InputContainer className="gap-0 border-transparent py-0">
                              <Label htmlFor="RepaymentType">
                                Repayment Type
                              </Label>
                              <InputSelect
                                initialValue={
                                  LoanItem?.Liability?.RepaymentType
                                }
                                onChange={(value) =>
                                  handleLoanChange({
                                    ...LoanItem,
                                    Liability: {
                                      ...LoanItem?.Liability,
                                      RepaymentType: value,
                                    },
                                  })
                                }
                                className="min-w-[242px]"
                              >
                                <Option value={null}></Option>
                                {[
                                  {
                                    value: "PrincipalInterest",
                                    label: "Principal & Interest",
                                  },
                                  {
                                    value: "InterestOnly",
                                    label: "Interest-Only",
                                  },
                                ].map((type) => (
                                  <Option value={type.value} key={type.value}>
                                    {type.label}
                                  </Option>
                                ))}
                              </InputSelect>
                            </InputContainer>
                            <InputContainer className="gap-0 border-transparent py-0">
                              <Label htmlFor="StartDate">Start Date</Label>
                              <InputDate
                                id="StartDate"
                                wrapperClassName="min-w-[242px]"
                                placeholder="DD / MM / YYYY"
                                value={
                                  LoanItem?.Liability?.StartDate
                                    ? parse(
                                        LoanItem?.Liability.StartDate,
                                        "yyyy-MM-dd",
                                        new Date()
                                      )
                                    : undefined
                                }
                                onChange={(date) =>
                                  handleLoanChange({
                                    ...LoanItem,
                                    Liability: {
                                      ...LoanItem?.Liability,
                                      StartDate: format(date, "yyyy-MM-dd"),
                                    },
                                  })
                                }
                              />
                            </InputContainer>
                            <InputContainer className="gap-0 border-transparent py-0">
                              <Label htmlFor="InterestOnlyTermYears">
                                Interest-Only Term
                              </Label>
                              <div className="grid min-w-[242px] grid-cols-2 gap-2">
                                <div className="relative">
                                  <InputNumber
                                    id="InterestOnlyTermYears"
                                    value={
                                      LoanItem?.Liability
                                        ?.InterestOnlyTermYears ?? 0
                                    }
                                    onChange={(value) =>
                                      handleLoanChange({
                                        ...LoanItem,
                                        Liability: {
                                          ...LoanItem?.Liability,
                                          InterestOnlyTermYears: value,
                                        },
                                      })
                                    }
                                    buttons={false}
                                    placeholder="0"
                                    pattern="^[0-9]*$"
                                  />
                                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                                    YRS
                                  </span>
                                </div>
                                <div className="relative">
                                  <InputNumber
                                    id="InterestOnlyTermMonths"
                                    value={
                                      LoanItem?.Liability
                                        ?.InterestOnlyTermMonths ?? undefined
                                    }
                                    onChange={(value) =>
                                      handleLoanChange({
                                        ...LoanItem,
                                        Liability: {
                                          ...LoanItem?.Liability,
                                          InterestOnlyTermMonths: value,
                                        },
                                      })
                                    }
                                    buttons={false}
                                    placeholder="0"
                                    pattern="^[0-9]*$"
                                  />
                                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                                    MOS
                                  </span>
                                </div>
                              </div>
                            </InputContainer>
                            <InputContainer className="gap-0 border-transparent py-0">
                              <Label htmlFor="IsRevolvingCredit">
                                Revolving Credit / Redraw Facility
                              </Label>
                              <InputSwitch
                                id="IsRevolvingCredit"
                                value={LoanItem?.Liability?.IsRevolvingCredit}
                                onChange={(value) =>
                                  handleLoanChange({
                                    ...LoanItem,
                                    Liability: {
                                      ...LoanItem?.Liability,
                                      IsRevolvingCredit: value,
                                    },
                                  })
                                }
                                className="min-w-[242px]"
                              >
                                <InputSwitch.InputSwitchOption value={true}>
                                  Yes
                                </InputSwitch.InputSwitchOption>
                                <InputSwitch.InputSwitchOption value={false}>
                                  No
                                </InputSwitch.InputSwitchOption>
                              </InputSwitch>
                            </InputContainer>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  )}
                  {LoanItem?.Lender !== null && (
                    <>
                      <InputContainer className="gap-0 border-transparent py-0">
                        <Label htmlFor="InterestRate" className="required">
                          Current Interest Rate
                        </Label>
                        <InputNumber
                          id="InterestRate"
                          className="min-w-[242px]"
                          withIcon={<Percent className="opacity-50" />}
                          iconPosition="right"
                          value={LoanItem?.Liability?.InterestRate || undefined}
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              Liability: {
                                ...LoanItem?.Liability,
                                InterestRate: value,
                              },
                            })
                          }
                        />
                      </InputContainer>
                      <InputContainer className="gap-0 border-transparent py-0">
                        <Label htmlFor="LendingAction" className="required">
                          Current Repayments
                        </Label>
                        <div className="flex min-w-[242px] items-center rounded-[3px] border border-[#C2B6CB]">
                          <InputNumber
                            id="RepaymentAmount"
                            withIcon={<Dollar />}
                            value={
                              LoanItem?.Liability?.RepaymentAmount || undefined
                            }
                            className="rounded-none border-transparent border-r-[#C2B6CB] focus:!outline-none"
                            onChange={(value) => {
                              handleLoanChange({
                                ...LoanItem,
                                Liability: {
                                  ...LoanItem?.Liability,
                                  RepaymentAmount: value,
                                },
                              });
                            }}
                            autonumeric
                          />
                          <InputSelect
                            initialValue={
                              LoanItem?.Liability?.RepaymentFrequency
                            }
                            onChange={(value) =>
                              handleLoanChange({
                                ...LoanItem,
                                Liability: {
                                  ...LoanItem?.Liability,
                                  RepaymentFrequency: value,
                                },
                              })
                            }
                            className="w-[50px]"
                            border="border-0"
                            formatButtonLabel={(value) => value?.charAt(0)}
                          >
                            <Option value={null}></Option>
                            {frequency.map((freq) => (
                              <Option value={freq.value} key={freq.value}>
                                {freq.label}
                              </Option>
                            ))}
                          </InputSelect>
                        </div>
                      </InputContainer>
                      <InputContainer className="gap-0 border-transparent py-0">
                        <Label htmlFor="Balance" className="required">
                          Current Balance
                        </Label>
                        <InputNumber
                          id="Balance"
                          buttons={false}
                          value={LoanItem?.Liability?.Balance ?? NaN}
                          onChange={(value) =>
                            handleLoanChange({
                              ...LoanItem,
                              Liability: {
                                ...LoanItem?.Liability,
                                Balance: value,
                              },
                            })
                          }
                          withIcon={<Dollar />}
                          className="min-w-[242px]"
                          autonumeric
                        />
                      </InputContainer>
                      <InputContainer className="justify-end gap-0 border-transparent py-0">
                        <Label
                          htmlFor={"ShowMore" + LoanItem.LoanId}
                          className="w-[242px] font-bold text-accent"
                        >
                          {showMore ? "Show Less" : "Show More"}...
                        </Label>
                        <input
                          checked={showMore}
                          onChange={() => setShowMore(!showMore)}
                          type="checkbox"
                          className="hidden"
                          id={"ShowMore" + LoanItem.LoanId}
                          name={"ShowMore" + LoanItem.LoanId}
                        />
                      </InputContainer>
                      <AnimatePresence>
                        {showMore && (
                          <motion.div
                            initial={{
                              height: 0,
                              opacity: 0,
                            }}
                            animate={{
                              height: "auto",
                              opacity: 1,
                            }}
                            exit={{ height: 0, opacity: 0, marginTop: 0 }}
                            transition={{ duration: 0.2, ease: "linear" }}
                            className="!mt-0 space-y-2"
                          >
                            <SectionLabel className="!text-[13px] desktop:!text-sm">
                              Existing Loan Details
                            </SectionLabel>
                            <InputContainer className="gap-0 border-transparent py-0">
                              <Label htmlFor="RepaymentType">
                                Repayment Type
                              </Label>
                              <InputSelect
                                initialValue={
                                  LoanItem?.Liability?.RepaymentType
                                }
                                onChange={(value) =>
                                  handleLoanChange({
                                    ...LoanItem,
                                    Liability: {
                                      ...LoanItem?.Liability,
                                      RepaymentType: value,
                                    },
                                  })
                                }
                                className="min-w-[242px]"
                              >
                                <Option value={null}></Option>
                                {[
                                  {
                                    value: "PrincipalInterest",
                                    label: "Principal & Interest",
                                  },
                                  {
                                    value: "InterestOnly",
                                    label: "Interest-Only",
                                  },
                                ].map((type) => (
                                  <Option value={type.value} key={type.value}>
                                    {type.label}
                                  </Option>
                                ))}
                              </InputSelect>
                            </InputContainer>
                            <InputContainer className="gap-0 border-transparent py-0">
                              <Label htmlFor="StartDate">Start Date</Label>
                              <InputDate
                                id="StartDate"
                                wrapperClassName="min-w-[242px]"
                                placeholder="DD / MM / YYYY"
                                value={
                                  LoanItem?.Liability?.StartDate
                                    ? parse(
                                        LoanItem?.Liability.StartDate,
                                        "yyyy-MM-dd",
                                        new Date()
                                      )
                                    : undefined
                                }
                                onChange={(date) =>
                                  handleLoanChange({
                                    ...LoanItem,
                                    Liability: {
                                      ...LoanItem?.Liability,
                                      StartDate: format(date, "yyyy-MM-dd"),
                                    },
                                  })
                                }
                              />
                            </InputContainer>
                            <InputContainer className="gap-0 border-transparent py-0">
                              <Label htmlFor="InterestOnlyTermYears">
                                Interest-Only Term
                              </Label>
                              <div className="grid min-w-[242px] grid-cols-2 gap-2">
                                <div className="relative">
                                  <InputNumber
                                    id="InterestOnlyTermYears"
                                    value={
                                      LoanItem?.Liability
                                        ?.InterestOnlyTermYears ?? 0
                                    }
                                    onChange={(value) =>
                                      handleLoanChange({
                                        ...LoanItem,
                                        Liability: {
                                          ...LoanItem?.Liability,
                                          InterestOnlyTermYears: value,
                                        },
                                      })
                                    }
                                    buttons={false}
                                    placeholder="0"
                                    pattern="^[0-9]*$"
                                  />
                                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                                    YRS
                                  </span>
                                </div>
                                <div className="relative">
                                  <InputNumber
                                    id="InterestOnlyTermMonths"
                                    value={
                                      LoanItem?.Liability
                                        ?.InterestOnlyTermMonths ?? undefined
                                    }
                                    onChange={(value) =>
                                      handleLoanChange({
                                        ...LoanItem,
                                        Liability: {
                                          ...LoanItem?.Liability,
                                          InterestOnlyTermMonths: value,
                                        },
                                      })
                                    }
                                    buttons={false}
                                    placeholder="0"
                                    pattern="^[0-9]*$"
                                  />
                                  <span className="pointer-events-none absolute right-2 top-1/2 -translate-y-1/2 font-helvetica text-[11px] text-[#6F5F7B]">
                                    MOS
                                  </span>
                                </div>
                              </div>
                            </InputContainer>
                            <InputContainer className="gap-0 border-transparent py-0">
                              <Label htmlFor="IsRevolvingCredit">
                                Revolving Credit / Redraw Facility
                              </Label>
                              <InputSwitch
                                id="IsRevolvingCredit"
                                value={LoanItem?.Liability?.IsRevolvingCredit}
                                onChange={(value) =>
                                  handleLoanChange({
                                    ...LoanItem,
                                    Liability: {
                                      ...LoanItem?.Liability,
                                      IsRevolvingCredit: value,
                                    },
                                  })
                                }
                                className="min-w-[242px]"
                              >
                                <InputSwitch.InputSwitchOption value={true}>
                                  Yes
                                </InputSwitch.InputSwitchOption>
                                <InputSwitch.InputSwitchOption value={false}>
                                  No
                                </InputSwitch.InputSwitchOption>
                              </InputSwitch>
                            </InputContainer>
                          </motion.div>
                        )}
                      </AnimatePresence>
                    </>
                  )}
                </>
              )}
            </>
          )}
        </div>
        {hasValueChanged && (
          <footer className="mt-[2px] flex items-center space-x-2 rounded-b-md bg-white p-[15px]">
            <Button
              type="button"
              size="medium"
              className="max-w-[83px] border border-transparent bg-transparent text-[#7B5C8D] hover:border-[#7B5C8D] desktop:text-sm"
              onClick={(e) => {
                e.preventDefault();
                onEditing?.(false);
                setLoanItem(values?.Loans[index]);
              }}
            >
              Cancel
            </Button>
            <Button
              size="medium"
              className="max-w-[83px] disabled:cursor-not-allowed disabled:bg-[#C2B6CB] desktop:text-sm"
              disabled={hasErrors}
              onClick={(e) => {
                e.stopPropagation();
                e.preventDefault();
                handleSave();
              }}
            >
              Save
            </Button>
          </footer>
        )}
      </Collapse>
    </>
  );
};

const LoansForm = ({
  initialValues,
  onSubmit,
  onChange,
  onEditing,
  ...rest
}: LoansFormProps) => {
  const [isEditing, setIsEditing] = useState(false);
  const [isAdding, setIsAdding] = useState(
    (initialValues.Loans || []).length === 0
  );

  const handleOnEditing = (status: boolean) => {
    setIsEditing(status);
    onEditing?.(status);
  };

  return (
    <section className="h-full bg-[#F5F1F8] px-[15px] py-[20px]">
      <Formik initialValues={initialValues} onSubmit={onSubmit} {...rest}>
        {({ values }) => (
          <Form>
            <p className="mb-4 text-[15px] font-bold text-[#736878]">
              Linked Loans
            </p>
            <FieldArray name="Loans">
              {({ remove, push }) => (
                <div className="grid gap-1">
                  <div className="grid">
                    {(values.Loans || []).map((Loan: any, index: number) => {
                      return (
                        <div className="mb-2" key={index}>
                          <LoanItemForm
                            index={index}
                            remove={remove}
                            disabled={isAdding}
                            onChange={onChange}
                            onEditing={handleOnEditing}
                          />
                        </div>
                      );
                    })}
                  </div>
                  <AddLoanForm
                    Loans={values.Loans}
                    isEditing={isEditing}
                    onSave={(value) => {
                      push({
                        ...value,
                      });
                      onChange?.({ Loans: [...values.Loans, value] });
                    }}
                    onAdding={setIsAdding}
                  />
                </div>
              )}
            </FieldArray>
          </Form>
        )}
      </Formik>
    </section>
  );
};

export default LoansForm;
